import { useMemo } from 'react';
import { LandingButton } from '@features/landing/shared/ui/components/LandingButton';
import { LandingPanel } from '@features/landing/shared/ui/components/LandingPanel';
import { LandingSection } from '@features/landing/shared/ui/components/LandingSection';
import { LandingSectionTitle } from '@features/landing/shared/ui/components/LandingSectionTitle';
import {
  PremiumPanel,
  PremiumPanelHorizontal,
} from '@features/landing/shared/ui/components/PremiumPanel';
import styles from './PremiumSection.module.css';
import { usePremiumLinksCtx } from '@features/landing/shared/providers/PremiumLinksProvider';
import { useMediaQuery } from '@providers/MediaQueryProvider';

const TEXT_PER_TRNMNT_EXPIRATION = 'действует до 31.12.2026';
const TEXT_PER_TRNMNT_SEANCE = (
  <>
    доступ к сеансам одновременной игры <sup>*</sup>
  </>
);

export function PremiumSection() {
  const device = useMediaQuery();
  const ctx = usePremiumLinksCtx();

  const studentMonthlyDiscount = useMemo(
    () =>
      ctx.monthlyOldPrice
        ? {
            oldAmount: <>{ctx.monthlyOldPrice}&nbsp;₽</>,
            chip: 'Цена для учеников РШШ',
          }
        : undefined,
    [ctx],
  );

  const studentYearlyDiscount = useMemo(
    () =>
      ctx.yearlyOldPrice
        ? {
            oldAmount: <>{ctx.yearlyOldPrice}&nbsp;₽</>,
            chip: 'Цена для учеников РШШ',
          }
        : undefined,
    [ctx],
  );

  const studentOneTournamentDiscount = useMemo(
    () =>
      ctx.oneTournamentOldPrice
        ? {
            oldAmount: <>{ctx.oneTournamentOldPrice}&nbsp;₽</>,
            chip: 'Цена для учеников РШШ',
          }
        : undefined,
    [ctx],
  );

  const studentFiveTournamentsDiscount = useMemo(
    () =>
      ctx.fiveTournamentsOldPrice
        ? {
            oldAmount: <>{ctx.fiveTournamentsOldPrice}&nbsp;₽</>,
            chip: 'Цена для учеников РШШ',
          }
        : undefined,
    [ctx],
  );

  const studentTenTournamentsDiscount = useMemo(
    () =>
      ctx.tenTournamentsOldPrice
        ? {
            oldAmount: <>{ctx.tenTournamentsOldPrice}&nbsp;₽</>,
            chip: 'Цена для учеников РШШ',
          }
        : undefined,
    [ctx],
  );

  const yearlyDiscount = (
    <>
      <span className={styles.discountDesk}>Скидка 30%</span>
      <span className={styles.discountNonDesk}>-30%</span>
    </>
  );

  return (
    <LandingSection id="prices" className={styles.root}>
      <LandingPanel preset="dark">
        <LandingSectionTitle preset="dark">
          Купить премиум-аккаунт
        </LandingSectionTitle>

        <div className={styles.panels}>
          <div className={styles.panelsRow}>
            <PremiumPanel
              className={styles.panel}
              title={
                <>
                  Премиум-аккаунт на месяц
                  <br />
                  <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                </>
              }
              studentDiscount={studentMonthlyDiscount}
              amount={<>{ctx.monthlyPrice}&nbsp;₽</>}
              description="8 — 10 турниров в месяц"
              advances={[
                <>
                  сеанс одновременной игры с&nbsp;гроссмейстером или
                  <br /> международным мастером
                </>,
              ]}
              action={
                <LandingButton
                  size="22r-40"
                  color="active"
                  href={ctx.monthlyLink}
                >
                  Купить
                </LandingButton>
              }
            />

            <PremiumPanel
              className={styles.panel}
              title={
                <>
                  Премиум-аккаунт на год
                  <br />
                  <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                </>
              }
              studentDiscount={studentYearlyDiscount}
              discount={yearlyDiscount}
              amount={<>{ctx.yearlyPrice}&nbsp;₽</>}
              description="102 турнира в год"
              advances={[
                <>
                  12 сеансов одновременной игры с&nbsp;гроссмейстером или
                  <br /> международным мастером
                </>,
              ]}
              action={
                <LandingButton
                  size="22r-40"
                  color="active"
                  href={ctx.yearlyLink}
                >
                  Купить
                </LandingButton>
              }
            />
          </div>

          {device !== 'mobile' && (
            <div className={styles.panelsRow}>
              <PremiumPanelHorizontal
                className={styles.panel}
                size="small"
                title={
                  <>
                    Премиум-аккаунт на&nbsp;все&nbsp;лето
                    <br />
                    <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                  </>
                }
                description="48 турниров за лето"
                advances={[
                  <>
                    3 сеанса одновременной игры с&nbsp;гроссмейстером или
                    международным мастером
                  </>,
                ]}
                amount={<>{ctx.summerPrice}&nbsp;₽</>}
                action={
                  <LandingButton
                    size="22r-40"
                    color="active"
                    href={ctx.summerLink}
                  >
                    Купить
                  </LandingButton>
                }
              />
            </div>
          )}

          <div className={styles.panelsRow}>
            {device === 'mobile' && (
              <PremiumPanel
                className={styles.panel}
                size="small"
                title={
                  <>
                    Премиум-аккаунт на все лето
                    <br />
                    <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                  </>
                }
                description="48 турниров за лето"
                advances={[
                  <>
                    3 сеанса одновременной игры с&nbsp;гроссмейстером или
                    международным мастером
                  </>,
                ]}
                amount={<>{ctx.summerPrice}&nbsp;₽</>}
                action={
                  <LandingButton
                    size="22r-40"
                    color="active"
                    href={ctx.summerLink}
                  >
                    Купить
                  </LandingButton>
                }
              />
            )}

            <PremiumPanel
              size="small"
              className={styles.panel}
              title={
                <>
                  1 турнир
                  <br />
                  <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                </>
              }
              studentDiscount={studentOneTournamentDiscount}
              amount={<>{ctx.oneTournamentPrice}&nbsp;₽</>}
              description={TEXT_PER_TRNMNT_EXPIRATION}
              advances={[TEXT_PER_TRNMNT_SEANCE]}
              action={
                <LandingButton
                  size="22r-40"
                  color="active"
                  href={ctx.oneTournamentLink}
                >
                  Купить
                </LandingButton>
              }
            />

            <PremiumPanel
              size="small"
              className={styles.panel}
              title={
                <>
                  5 турниров
                  <br />
                  <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                </>
              }
              studentDiscount={studentFiveTournamentsDiscount}
              amount={<>{ctx.fiveTournamentsPrice}&nbsp;₽</>}
              description={TEXT_PER_TRNMNT_EXPIRATION}
              advances={[TEXT_PER_TRNMNT_SEANCE]}
              action={
                <LandingButton
                  size="22r-40"
                  color="active"
                  href={ctx.fiveTournamentsLink}
                >
                  Купить
                </LandingButton>
              }
            />

            <PremiumPanel
              size="small"
              className={styles.panel}
              title={
                <>
                  10 турниров
                  <br />
                  <span className={styles.subtitle}>Юниоры · до 19 лет</span>
                </>
              }
              studentDiscount={studentTenTournamentsDiscount}
              amount={<>{ctx.tenTournamentsPrice}&nbsp;₽</>}
              description={TEXT_PER_TRNMNT_EXPIRATION}
              advances={[TEXT_PER_TRNMNT_SEANCE]}
              action={
                <LandingButton
                  size="22r-40"
                  color="active"
                  href={ctx.tenTournamentsLink}
                >
                  Купить
                </LandingButton>
              }
            />
          </div>
        </div>

        <div className={styles.description}>
          <div className={styles.descriptionItem}>
            Сеанс одновременной игры проводят тренеры РШШ Николай Чадаев (МГ),
            Сергей Юдин (МГ), Николай Коновалов (МГ), Ярослав Улько (ММ), Борис
            Офицерьян (ММ) и приглашенные гроссмейстеры.
          </div>
          <div className={styles.descriptionItem}>
            <sup>*</sup> При реализации абонемента по числу турниров, сеанс
            одновременной игры засчитывается как турнир.
          </div>
          {/* Скрываем, т.к. из контекста непонятно про какое событие речь.
              На деле подписка активируется с ближайшего премиального события
              (не только гран-при)
          */}
          {/* <div className={styles.descriptionItem}>
            Подписка активируется в день ближайшего турнира, поэтому вам не
            нужно ждать, чтобы оплатить премиум!
          </div> */}
        </div>
      </LandingPanel>
    </LandingSection>
  );
}
